import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Eraser, PenLine, Type as TypeIcon } from "lucide-react";

// SignaturePad — draw on canvas OR type your name. Returns data URL on submit.
// Used to digitally sign a contract.
export default function SignaturePad({ open, onClose, onSign, signerNameDefault }) {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState("draw"); // "draw" | "type"
  const [typedText, setTypedText] = useState(signerNameDefault || "");
  const [hasDrawn, setHasDrawn] = useState(false);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!open) return;
    setHasDrawn(false);
    setTypedText(signerNameDefault || "");
    setMode("draw");
    // Wait a tick for canvas mount
    setTimeout(() => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.strokeStyle = "#0F172A";
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }, 30);
  }, [open, signerNameDefault]);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };

  const start = (e) => { drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault?.();
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    setHasDrawn(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const submit = () => {
    if (mode === "draw") {
      if (!hasDrawn) return;
      const c = canvasRef.current;
      const dataUrl = c.toDataURL("image/png");
      onSign({ signature_kind: "drawn", signature_image: dataUrl, signer_name: signerNameDefault });
    } else {
      if (!typedText.trim()) return;
      onSign({ signature_kind: "typed", signature_image: typedText.trim(), signer_name: typedText.trim() });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm max-w-xl">
        <DialogHeader><DialogTitle className="font-display">Sign this contract</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-xs text-slate-500">Your signature is timestamped and stored alongside this contract. You can either draw with your mouse/finger or type your full name in cursive.</p>
          <div className="flex items-center gap-2">
            <button onClick={() => setMode("draw")} className={`text-xs px-3 py-1.5 rounded-sm border inline-flex items-center gap-1 ${mode === "draw" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid="sig-mode-draw"><PenLine size={12} />Draw</button>
            <button onClick={() => setMode("type")} className={`text-xs px-3 py-1.5 rounded-sm border inline-flex items-center gap-1 ${mode === "type" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`} data-testid="sig-mode-type"><TypeIcon size={12} />Type</button>
          </div>
          {mode === "draw" ? (
            <div>
              <div className="border border-slate-300 rounded-sm bg-white" style={{ touchAction: "none" }}>
                <canvas
                  ref={canvasRef}
                  width={560}
                  height={180}
                  onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                  onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                  className="w-full h-44 cursor-crosshair"
                  data-testid="sig-canvas"
                />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-[11px] text-slate-500 italic">Sign above with your mouse or finger.</span>
                <Button variant="outline" size="sm" onClick={clear} className="rounded-sm h-7" data-testid="sig-clear"><Eraser size={12} className="mr-1" />Clear</Button>
              </div>
            </div>
          ) : (
            <div>
              <Label className="label-overline">Type your full legal name</Label>
              <Input
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                className="rounded-sm mt-1 text-3xl py-6"
                style={{ fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive" }}
                placeholder="Your full name"
                data-testid="sig-type-input"
              />
              <p className="text-[11px] text-slate-500 italic mt-2">Typed signatures are legally equivalent in most jurisdictions when paired with consent + timestamp + IP.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button
            onClick={submit}
            disabled={mode === "draw" ? !hasDrawn : !typedText.trim()}
            className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white disabled:opacity-50"
            data-testid="sig-submit"
          >I agree & sign</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
